import React, {memo, useMemo} from 'react'
import {KTSVG, toAbsoluteUrl} from '../../../../../../_metronic/helpers'
import {useAuth} from '../../../../../modules/auth'
import {NotImported} from '../../../../components/NotInformat'
import styles from './boardUnitConsumers.module.css'

type Props = {
  unitConsumers: any[]
  setMyUnitConsumer: (unitConsumer: any) => void
  setShowCreateAppModal: (show: boolean) => void
  setShowCreateAppModalDelete: (show: boolean) => void
  onCreateUnitConsumer: () => void
  onOpenPlant: (plantId: string) => void
  onOpenConcessionaire: (unitConsumer: any) => void
}

type PlantGroup = {
  plantId: string
  plantName: string
  plano: string
  units: any[]
}

const typeBadgeColor = (type?: string) => {
  if (type === 'GERADORA') return 'primary'
  if (type === 'CONSUMIDORA') return 'info'
  return 'secondary'
}

const statusColor = (status?: string) => {
  if (status === 'ATIVA') return 'success'
  if (status === 'INATIVA') return 'danger'
  return 'secondary'
}

const DEFAULT_CONCESSIONAIRE_IMAGE =
  'https://res.cloudinary.com/deq3ehxe3/image/upload/v1771533247/1275576_zn46s3.png'

export const getConcessionaireImageSrc = (linkImagem?: string | null) => {
  if (linkImagem) {
    return linkImagem.startsWith('http') ? linkImagem : toAbsoluteUrl(linkImagem)
  }

  return toAbsoluteUrl(DEFAULT_CONCESSIONAIRE_IMAGE)
}

export const groupUnitConsumersByPlant = (unitConsumers: any[]): PlantGroup[] => {
  const plantsMap = new Map<string, PlantGroup>()

  unitConsumers.forEach((unitConsumer) => {
    const plant = unitConsumer.plant
    const plantId = plant?.id || 'sem-usina'

    if (!plantsMap.has(plantId)) {
      plantsMap.set(plantId, {
        plantId,
        plantName: plant?.plantName || plant?.name || 'Sem usina',
        plano: plant?.plano || '',
        units: [],
      })
    }

    plantsMap.get(plantId)!.units.push(unitConsumer)
  })

  return Array.from(plantsMap.values())
    .map((plant) => ({
      ...plant,
      units: [...plant.units].sort((a, b) => {
        if (a.type_unit_consumer !== b.type_unit_consumer) {
          if (a.type_unit_consumer === 'GERADORA') return -1
          if (b.type_unit_consumer === 'GERADORA') return 1
        }
        return (a.name || '').localeCompare(b.name || '', 'pt-BR')
      }),
    }))
    .sort((a, b) => {
      if (a.plantId === 'sem-usina') return 1
      if (b.plantId === 'sem-usina') return -1
      return a.plantName.localeCompare(b.plantName, 'pt-BR')
    })
}

export const BoardUnitConsumers = memo(function BoardUnitConsumers({
  unitConsumers,
  setMyUnitConsumer,
  setShowCreateAppModal,
  setShowCreateAppModalDelete,
  onCreateUnitConsumer,
  onOpenPlant,
  onOpenConcessionaire,
}: Props) {
  const user = useAuth()
  const plantGroups = useMemo(() => groupUnitConsumersByPlant(unitConsumers || []), [unitConsumers])

  const canEdit = !!user.currentUser?.role?.slug && user.currentUser.role.slug !== 'CLIENTE'
  const isPremium = user.currentUser?.companies?.[0]?.planos === 'PREMIUM'

  const handleOpenUnit = (unitConsumer: any) => {
    if (!canEdit) return
    setMyUnitConsumer({
      ...unitConsumer,
      idPlant: unitConsumer.plant?.id || null,
      idConcessionarie: unitConsumer.concessioraire?.id || null,
    })
    setShowCreateAppModal(true)
  }

  const handleDeleteUnit = (event: React.MouseEvent, unitConsumer: any) => {
    event.preventDefault()
    event.stopPropagation()
    setMyUnitConsumer(unitConsumer)
    setShowCreateAppModalDelete(true)
  }

  if (!unitConsumers || unitConsumers.length === 0) {
    return (
      <div className={styles.emptyState}>
        <KTSVG
          path='/media/icons/duotune/general/gen001.svg'
          className='svg-icon-2hx svg-icon-muted'
        />
        <span className={styles.emptyTitle}>Nenhuma unidade consumidora encontrada</span>
        <span className={styles.emptyHint}>
          Não há unidades para os filtros selecionados. Ajuste a busca, a usina ou a concessionária
          e tente novamente.
        </span>
      </div>
    )
  }

  return (
    <div className={styles.boardGrid}>
      {plantGroups.map((plant) => {
        const generatorsCount = plant.units.filter(
          (unit) => unit.type_unit_consumer === 'GERADORA'
        ).length
        const consumersCount = plant.units.length - generatorsCount

        return (
          <article key={plant.plantId} className={styles.plantCard}>
            <header className={styles.plantHeader}>
              <img
                src={toAbsoluteUrl('/media/imgs/plant2.png')}
                alt=''
                className={styles.plantIcon}
              />
              <div className={styles.plantTitle}>
                <div className={styles.plantTitleRow}>
                  {plant.plantId !== 'sem-usina' ? (
                    <button
                      type='button'
                      className={`${styles.plantName} ${styles.linkButton}`}
                      title={`Ver detalhes da usina ${plant.plantName}`}
                      onClick={() => onOpenPlant(plant.plantId)}
                    >
                      {plant.plantName}
                    </button>
                  ) : (
                    <span className={styles.plantName} title={plant.plantName}>
                      {plant.plantName}
                    </span>
                  )}
                  {plant.plano && (
                    <span className='badge badge-light-secondary fs-9 fw-semibold'>
                      {plant.plano}
                    </span>
                  )}
                </div>
                <div className={styles.plantMeta}>
                  <span className='badge badge-light fs-8'>
                    {plant.units.length} {plant.units.length === 1 ? 'unidade' : 'unidades'}
                  </span>
                  {generatorsCount > 0 && (
                    <span className='badge badge-light-primary fs-8'>
                      {generatorsCount} {generatorsCount === 1 ? 'geradora' : 'geradoras'}
                    </span>
                  )}
                  {consumersCount > 0 && (
                    <span className='badge badge-light-info fs-8'>
                      {consumersCount} {consumersCount === 1 ? 'consumidora' : 'consumidoras'}
                    </span>
                  )}
                </div>
              </div>
              <div
                className={styles.flowInfo}
                tabIndex={0}
                aria-label='Como funciona o fluxo de energia'
              >
                <KTSVG
                  path='/media/icons/duotune/general/gen044.svg'
                  className='svg-icon-2 svg-icon-muted'
                />
                <div className={styles.flowTooltip} role='tooltip'>
                  <div className={styles.flowTooltipBox}>
                    <span className={styles.flowTooltipTitle}>Como funciona o fluxo</span>
                    <span>
                      Cada usina possui <strong>1 unidade geradora</strong> e{' '}
                      <strong>X unidades consumidoras</strong>.
                    </span>
                    <span>
                      A energia gerada pela usina é injetada na geradora, e o excedente é
                      distribuído entre as consumidoras.
                    </span>
                    <span>
                      Uma unidade consumidora pode ser criada e vinculada à usina pelo botão{' '}
                      {canEdit ? (
                        <button
                          type='button'
                          className={styles.flowTooltipLink}
                          onClick={onCreateUnitConsumer}
                        >
                          "Nova Unidade Consumidora"
                        </button>
                      ) : (
                        <strong>"Nova Unidade Consumidora"</strong>
                      )}
                      .
                    </span>
                  </div>
                </div>
              </div>
            </header>

            <div className={styles.unitsWrap}>
              {plant.units.map((unit) => (
                <section
                  key={unit.id}
                  className={`${styles.unitItem} bg-body ${
                    canEdit ? styles.unitItemClickable : ''
                  }`}
                  onClick={() => handleOpenUnit(unit)}
                >
                  <div className={styles.unitTitleRow}>
                    <div className={styles.unitIcon}>
                      <KTSVG
                        path='/media/icons/duotune/general/gen001.svg'
                        className='svg-icon-2x svg-icon-muted'
                      />
                      {isPremium && (
                        <span
                          className={`bg-${statusColor(unit.status)} ${styles.statusDot}`}
                          title={unit.status ? `Monit. Autom.: ${unit.status}` : 'Monit. Autom.'}
                        />
                      )}
                    </div>
                    <div className={styles.unitName}>
                      {unit.name || <NotImported className='fs-8' />}
                    </div>
                    <span
                      className={`badge badge-light-${typeBadgeColor(
                        unit.type_unit_consumer
                      )} fs-8 fw-semibold ${styles.unitTypeBadge}`}
                    >
                      {unit.type_unit_consumer || <NotImported className='fs-8' />}
                    </span>
                  </div>

                  <div className={styles.unitSubRow}>
                    <div className={styles.concessionaireInfo}>
                      {unit.concessioraire ? (
                        <>
                          <img
                            src={getConcessionaireImageSrc(unit.concessioraire.link_imagem)}
                            alt={unit.concessioraire.name}
                            title={unit.concessioraire.name}
                            className={styles.concessionaireImage}
                          />
                          {canEdit ? (
                            <button
                              type='button'
                              className={`${styles.concessionaireName} ${styles.linkButton}`}
                              title='Editar concessionária'
                              onClick={(event) => {
                                event.stopPropagation()
                                onOpenConcessionaire(unit)
                              }}
                            >
                              {unit.concessioraire.name}
                            </button>
                          ) : (
                            <span className={styles.concessionaireName}>
                              {unit.concessioraire.name}
                            </span>
                          )}
                        </>
                      ) : (
                        <NotImported className='fs-8' />
                      )}
                    </div>

                    <div className={styles.unitSide}>
                      {unit.contrato && (
                        <span className={styles.unitMetaValue} title='Número do contrato'>
                          Nº {unit.contrato}
                        </span>
                      )}
                      <div className={styles.unitActions}>
                        {canEdit && (
                          <button
                            type='button'
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            style={{width: '1.6rem', height: '1.6rem'}}
                            title='Editar unidade'
                            onClick={(event) => {
                              event.stopPropagation()
                              handleOpenUnit(unit)
                            }}
                          >
                            <KTSVG
                              path='/media/icons/duotune/art/art005.svg'
                              className='svg-icon-5'
                            />
                          </button>
                        )}
                        {unit.type_unit_consumer && unit.type_unit_consumer !== 'GERADORA' && (
                          <button
                            type='button'
                            className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                            style={{width: '1.6rem', height: '1.6rem'}}
                            title='Apagar unidade'
                            onClick={(event) => handleDeleteUnit(event, unit)}
                          >
                            <KTSVG
                              path='/media/icons/duotune/general/gen027.svg'
                              className='svg-icon-5'
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              ))}
            </div>
          </article>
        )
      })}
    </div>
  )
})
